import { useState, useEffect } from "react";
import { Link } from "react-router-dom";
import api from "../lib/api";
import { Trophy, DollarSign, Archive, Target, Medal, Crown, Award, Sparkles } from "lucide-react";

const SORT_TABS = [
  { key: "value", label: "Total Value", icon: DollarSign, testid: "lb-sort-value" },
  { key: "count", label: "Unique Models", icon: Archive, testid: "lb-sort-count" },
  { key: "completion", label: "Completion %", icon: Target, testid: "lb-sort-completion" },
  { key: "contributors", label: "Catalog Contributors", icon: Sparkles, testid: "lb-sort-contributors" },
];

function RankMedal({ rank }) {
  if (rank === 1) return <Crown className="w-5 h-5 text-amber-400 drop-shadow-[0_0_8px_rgba(251,191,36,0.5)]" />;
  if (rank === 2) return <Medal className="w-5 h-5 text-zinc-300" />;
  if (rank === 3) return <Award className="w-5 h-5 text-orange-400" />;
  return <span className="font-mono text-sm text-zinc-500 w-5 text-center">#{rank}</span>;
}

function Avatar({ leader }) {
  if (leader.avatar_url) {
    return <img src={leader.avatar_url} alt="" className="w-10 h-10 rounded-full object-cover border border-zinc-800" />;
  }
  const initial = (leader.name || "?").charAt(0).toUpperCase();
  return (
    <div className="w-10 h-10 rounded-full bg-gradient-to-br from-rose-600 to-amber-500 flex items-center justify-center font-bold text-white text-sm">
      {initial}
    </div>
  );
}

function LeaderRow({ leader, rank, sort }) {
  const isContributors = sort === "contributors";
  const metricValue = isContributors
    ? `${leader.approved_count}`
    : sort === "value"
      ? `$${leader.total_value.toLocaleString(undefined, { maximumFractionDigits: 0 })}`
      : sort === "count"
        ? `${leader.unique_releases}`
        : `${leader.completion_percentage}%`;

  const metricLabel = isContributors
    ? (leader.approved_count === 1 ? "release" : "releases")
    : sort === "value" ? "value" : sort === "count" ? "models" : "complete";

  const subtext = isContributors
    ? (leader.last_approved_at
        ? `Last approved ${new Date(leader.last_approved_at).toLocaleDateString()}`
        : "Catalog contributor")
    : `${leader.unique_releases} models · ${leader.total_purchases} purchases · ${leader.completion_percentage}%`;

  const isTop3 = rank <= 3;
  const content = (
    <div
      data-testid={`leaderboard-row-${rank}`}
      className={`flex items-center gap-4 p-4 rounded-xl border transition-all ${
        isTop3
          ? "bg-gradient-to-r from-zinc-900/80 via-rose-950/10 to-zinc-900/80 border-white/10 hover:border-rose-500/30"
          : "bg-zinc-900/40 border-white/5 hover:border-zinc-700"
      }`}
    >
      <div className="flex-shrink-0 w-8 flex items-center justify-center">
        <RankMedal rank={rank} />
      </div>
      <Avatar leader={leader} />
      <div className="flex-1 min-w-0">
        <p className="text-sm font-medium text-zinc-100 truncate">{leader.name || "Anonymous"}</p>
        <p className="text-[11px] text-zinc-500 truncate">{subtext}</p>
      </div>
      <div className="text-right flex-shrink-0">
        <p className={`font-mono text-lg font-bold ${isContributors ? "text-purple-400" : "text-emerald-400"}`}>{metricValue}</p>
        <p className="text-[10px] text-zinc-600 uppercase tracking-wider">{metricLabel}</p>
      </div>
    </div>
  );

  // Contributors may not be public — only link if they are
  if (leader.profile_slug && (!isContributors || leader.is_public)) {
    return (
      <Link to={`/collector/${leader.profile_slug}`} data-testid={`leader-link-${leader.profile_slug}`}>
        {content}
      </Link>
    );
  }
  return content;
}

export default function LeaderboardPage() {
  const [sort, setSort] = useState("value");
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    let active = true;
    setLoading(true);
    (async () => {
      try {
        const { data } = await api.get(`/leaderboard?sort=${sort}&limit=20`);
        if (active) setData(data);
      } catch { /* silent */ }
      finally { if (active) setLoading(false); }
    })();
    return () => { active = false; };
  }, [sort]);

  const leaders = data?.leaders || [];

  return (
    <div data-testid="leaderboard-page" className="p-4 sm:p-6 lg:p-8 space-y-6 animate-fadeIn">
      {/* Hero */}
      <div className="bg-gradient-to-r from-zinc-900/80 via-amber-950/15 to-zinc-900/80 border border-white/5 rounded-2xl p-6 sm:p-8">
        <div className="flex items-center gap-4">
          <div className="p-3 rounded-xl bg-amber-500/10 border border-amber-500/20">
            <Trophy className="w-8 h-8 text-amber-400" />
          </div>
          <div>
            <h1 className="font-heading text-2xl sm:text-3xl font-bold text-zinc-100">Leaderboard</h1>
            <p className="text-zinc-500 text-sm mt-1">
              {sort === "contributors"
                ? "Top community curators — collectors who've grown the catalog."
                : "Top collectors across the community."}
            </p>
          </div>
        </div>
      </div>

      {/* Sort tabs */}
      <div data-testid="leaderboard-tabs" className="inline-flex bg-zinc-900/60 backdrop-blur-xl border border-white/5 rounded-xl p-1 flex-wrap gap-1">
        {SORT_TABS.map((t) => {
          const Icon = t.icon;
          const active = sort === t.key;
          return (
            <button
              key={t.key}
              data-testid={t.testid}
              onClick={() => setSort(t.key)}
              className={`flex items-center gap-2 px-4 py-2 rounded-lg text-sm font-medium transition-colors ${
                active ? "bg-zinc-800 text-zinc-100" : "text-zinc-500 hover:text-zinc-300"
              }`}
            >
              <Icon className="w-3.5 h-3.5" />{t.label}
            </button>
          );
        })}
      </div>

      {/* Board */}
      {loading ? (
        <div className="space-y-3">
          {["lb-skel-1", "lb-skel-2", "lb-skel-3", "lb-skel-4", "lb-skel-5"].map((id) => (
            <div key={id} className="h-20 bg-zinc-900/40 rounded-xl animate-pulse" />
          ))}
        </div>
      ) : leaders.length === 0 ? (
        <div className="text-center py-20">
          <Trophy className="w-12 h-12 text-zinc-700 mx-auto mb-3" />
          <p className="text-zinc-500 text-lg">
            {sort === "contributors" ? "No catalog contributions yet" : "No public collectors yet"}
          </p>
          <p className="text-zinc-600 text-sm mt-1">
            {sort === "contributors"
              ? "Suggest a release from the Catalog page or via the barcode scanner — once approved, you'll appear here."
              : "Enable public profile in your settings to compete!"}
          </p>
        </div>
      ) : (
        <div className="space-y-3">
          {leaders.map((leader, i) => (
            <LeaderRow key={leader.user_id} leader={leader} rank={i + 1} sort={sort} />
          ))}
        </div>
      )}

      {sort !== "contributors" && data?.total_releases != null && (
        <p className="text-center text-[11px] text-zinc-600">
          Completion based on {data.total_releases} tracked releases. Only collectors with public profiles appear.
        </p>
      )}
      {sort === "contributors" && leaders.length > 0 && (
        <p className="text-center text-[11px] text-zinc-600">
          Counts include only release suggestions that have been approved into the public catalog.
        </p>
      )}
    </div>
  );
}
